import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useBillList, useBillListExport, useDownloadBill, useViewBill } from "../../hooks";
import BillFilterForm from "./billFilterForm";
import { StatusBadge } from "./StatusBadge";
import { logEvent } from "@shared/analytics/analytics";
import { Download, Eye } from "lucide-react";

// Define proper TypeScript interface for bill filters
interface BillFilters {
  page?: number;
  limit?: number;
  search_data?: string;
  status?: string[];
  premise?: string[];
  consumer_type?: string[];
  bill_run_id?: string[];
  created_on_start?: string;
  created_on_end?: string;
  due_date_start?: string;
  due_date_end?: string;
  consumption_min?: number;
  consumption_max?: number;
  amount_min?: number;
  amount_max?: number;
  outstanding_min?: number;
  outstanding_max?: number;
}

interface BillTableProps {
  className?: string;
}

const BillTable: React.FC<BillTableProps> = ({ className }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const currency = useCurrency();

  // Parse filters from URL parameters - following ConsumerList pattern
  const getFiltersFromUrl = (): BillFilters => {
    const urlFilters: BillFilters = {
      limit: PAGE_SIZE,
    };

    // Parse search query
    const searchParam = searchParams.get("search");
    if (searchParam) {
      urlFilters.search_data = searchParam;
    }

    // Parse status array (multi-select)
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      urlFilters.status = statusParam.split(",");
    }

    // Parse premise type array (multi-select)
    const premiseParam = searchParams.get("premise");
    if (premiseParam && premiseParam !== "ALL_PREMISE_TYPES") {
      urlFilters.premise = premiseParam.split(",");
    }

    // Parse consumer type array (multi-select)
    const consumerParam = searchParams.get("consumer_type");
    if (consumerParam && consumerParam !== "ALL_CONSUMER_TYPES") {
      urlFilters.consumer_type = consumerParam.split(",");
    }

    // Parse bill cycle array (multi-select)
    const billCycleParam = searchParams.get("bill_run_id");
    if (billCycleParam && billCycleParam !== "ALL_BILL_CYCLES") {
      urlFilters.bill_run_id = billCycleParam.split(",");
    }

    // Parse date ranges
    const createdStartParam = searchParams.get("created_on_start");
    if (createdStartParam) {
      urlFilters.created_on_start = createdStartParam;
    }

    const createdEndParam = searchParams.get("created_on_end");
    if (createdEndParam) {
      urlFilters.created_on_end = createdEndParam;
    }

    const dueStartParam = searchParams.get("due_date_start");
    if (dueStartParam) {
      urlFilters.due_date_start = dueStartParam;
    }

    const dueEndParam = searchParams.get("due_date_end");
    if (dueEndParam) {
      urlFilters.due_date_end = dueEndParam;
    }

    // Parse numeric ranges
    const consumptionMinParam = searchParams.get("consumption_min");
    if (consumptionMinParam) {
      urlFilters.consumption_min = Number(consumptionMinParam);
    }

    const consumptionMaxParam = searchParams.get("consumption_max");
    if (consumptionMaxParam) {
      urlFilters.consumption_max = Number(consumptionMaxParam);
    }

    const amountMinParam = searchParams.get("amount_min");
    if (amountMinParam) {
      urlFilters.amount_min = Number(amountMinParam);
    }

    const amountMaxParam = searchParams.get("amount_max");
    if (amountMaxParam) {
      urlFilters.amount_max = Number(amountMaxParam);
    }

    const outstandingMinParam = searchParams.get("outstanding_min");
    if (outstandingMinParam) {
      urlFilters.outstanding_min = Number(outstandingMinParam);
    }

    const outstandingMaxParam = searchParams.get("outstanding_max");
    if (outstandingMaxParam) {
      urlFilters.outstanding_max = Number(outstandingMaxParam);
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<BillFilters>(getFiltersFromUrl);

  // Update filters when URL changes - following ConsumerList pattern
  useEffect(() => {
    setFilters(getFiltersFromUrl());
  }, [searchParams]);

  const exportFilters = useMemo(() => {
    const exportParams = { ...filters };
    delete exportParams.page;
    delete exportParams.limit;
    return exportParams;
  }, [filters]);

  const { data: billsData, isPending: isBillsLoading } = useBillList(
    filters,
    page
  );

  const { exportCSV, isExporting } = useBillListExport(exportFilters);

  const bills: [] = billsData?.result;
  const totalCount = billsData?.count;

  // Extract pagination info exactly like ConsumerList
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startItem = (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, totalCount);

  // Apply filters handler - following ConsumerList pattern
  const handleApplyFilters = (newFilters: Partial<BillFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    [
      "status",
      "premise",
      "consumer_type",
      "bill_run_id",
      "created_on_start",
      "created_on_end",
      "due_date_start",
      "due_date_end",
      "consumption_min",
      "consumption_max",
      "amount_min",
      "amount_max",
      "outstanding_min",
      "outstanding_max",
      "page",
    ].forEach((key) => {
      params.delete(key);
    });

    // Add new filter params
    if (newFilters.status && newFilters.status.length > 0) {
      params.set("status", newFilters.status.join(","));
    }
    if (newFilters.premise && newFilters.premise.length > 0) {
      params.set("premise", newFilters.premise.join(","));
    }
    if (newFilters.consumer_type && newFilters.consumer_type.length > 0) {
      params.set("consumer_type", newFilters.consumer_type.join(","));
    }
    if (newFilters.bill_run_id && newFilters.bill_run_id.length > 0) {
      params.set("bill_run_id", newFilters.bill_run_id.join(","));
    }
    if (newFilters.created_on_start) {
      params.set("created_on_start", newFilters.created_on_start);
    }
    if (newFilters.created_on_end) {
      params.set("created_on_end", newFilters.created_on_end);
    }
    if (newFilters.due_date_start) {
      params.set("due_date_start", newFilters.due_date_start);
    }
    if (newFilters.due_date_end) {
      params.set("due_date_end", newFilters.due_date_end);
    }
    if (
      newFilters.consumption_min !== undefined &&
      newFilters.consumption_min !== null
    ) {
      params.set("consumption_min", newFilters.consumption_min.toString());
    }
    if (
      newFilters.consumption_max !== undefined &&
      newFilters.consumption_max !== null
    ) {
      params.set("consumption_max", newFilters.consumption_max.toString());
    }
    if (newFilters.amount_min !== undefined && newFilters.amount_min !== null) {
      params.set("amount_min", newFilters.amount_min.toString());
    }
    if (newFilters.amount_max !== undefined && newFilters.amount_max !== null) {
      params.set("amount_max", newFilters.amount_max.toString());
    }
    if (
      newFilters.outstanding_min !== undefined &&
      newFilters.outstanding_min !== null
    ) {
      params.set("outstanding_min", newFilters.outstanding_min.toString());
    }
    if (
      newFilters.outstanding_max !== undefined &&
      newFilters.outstanding_max !== null
    ) {
      params.set("outstanding_max", newFilters.outstanding_max.toString());
    }
    setPage(1);
    setSearchParams(params);
  };

  // Reset filters handler - following ConsumerList pattern
  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter parameters
    [
      "status",
      "premise",
      "consumer_type",
      "bill_run_id",
      "created_on_start",
      "created_on_end",
      "due_date_start",
      "due_date_end",
      "consumption_min",
      "consumption_max",
      "amount_min",
      "amount_max",
      "outstanding_min",
      "outstanding_max",
      "page",
      "search",
    ].forEach((key) => {
      params.delete(key);
    });

    setPage(1);
    setSearchParams(params);
  };

  // Page change handler - following ConsumerList pattern
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Debounced search handler - following ConsumerList pattern
  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    // setPage(1);

    setSearchParams(params);
  };

  // Define table columns
  const columns: TableColumn[] = [
    {
      key: "billNumber",
      header: "Invoice Number",
      sortable: true,
      width: "w-[150px]",
    },
    {
      key: "consumerName",
      header: "Consumer",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium">{row.consumerName}</p>
          <p className="text-xs text-muted-foreground">
            {row.remoteConsumerNumber}
          </p>
        </div>
      ),
    },
    {
      key: "premise",
      header: "Premise",
      sortable: false,
    },
    {
      key: "billCycleName",
      header: "Bill Cycle",
      sortable: false,
    },
    {
      key: "billMonth",
      header: "Billing Month",
      sortable: false,
    },
    {
      key: "consumptions",
      header: "Consumption (Units)",
      sortable: true,
      render: (value) => `${value} unit`,
    },
    {
      key: "billAmount",
      header: "Billed Amount",
      sortable: true,
      render: (value) => `${currency} ${Number(value).toFixed(2)}`,
    },
    {
      key: "createdDate",
      header: "Generated Date",
      sortable: true,
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "custom",
      render: (value) => <StatusBadge status={value} />,
    },
  ];

  const handleExport = () => {
    exportCSV();
    logEvent('Bill List Exported');
  };

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();

  const handleDownloadClick = async (billId: string, filename?: string) => {
    try {
      await downloadBill.mutateAsync({
        billId,
        filename: filename || `bill-${billId}.pdf`,
      });
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };

  const actions = [
    {
      label: "View Bill",
      icon: <Eye />,
      onClick: (bill) => {
        logEvent(`BX Bill Viewed`, {
          id: bill.id
        })
        handleViewClick(bill.id);
      },
    },
    {
      label: "Download PDF",
      icon: <Download />,
      onClick: (bill) => {
        handleDownloadClick(bill.id, "bill.pdf");
        logEvent (`BX Bill Downloaded`, {
          billId: bill.id
        })
      },
    },
  ];

  const paginationConfig = {
    currentPage: page,
    totalPages,
    totalItems: totalCount,
    itemsPerPage: PAGE_SIZE,
    startItem,
    endItem,
  };

  return (
    <div className={className}>
      <GenericTable
        data={bills}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        searchPlaceholder="Search bills by account, or invoice number..."
        searchParamName="search"
        onSearch={handleSearch}
        filterPanel={
          <BillFilterForm
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        pagination={paginationConfig}
        onPageChange={handlePageChange}
        onExport={handleExport}
        emptyMessage="No bills found matching your filters"
      />
    </div>
  );
};

export default BillTable;
